import { Alert, ConfigProvider } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import apiClient from "../../helpers/apiClient";
import { useUser } from "../../contexts/UserContext/UserContext";
import useCarPricingMode from "../../features/cars/hooks/useCarPricingMode";
import BannerAlertMessage, { BANNER_ALERT_COMPACT_CLASS } from "./BannerAlertMessage";

/**
 * Guides the owner to complete category pricing when `pricingMode === 'category'`.
 * Calls `GET /api/car-category-v2/pricing-readiness-alert`; renders nothing in `car`
 * mode (pricing lives on the car) or when every category is pricing-ready.
 *
 * @param {{ refreshKey?: unknown }} props - change `refreshKey` to force a re-check.
 */
const CategoryPricingReadinessAlert = ({ refreshKey }) => {
    const { t } = useTranslation("banner");
    const history = useHistory();
    const { state, myCompanyData } = useUser();
    const isCarPricingMode = useCarPricingMode();

    const [showAlert, setShowAlert] = useState(false);

    useEffect(() => {
        if (!state?.isAuthenticated || !myCompanyData) {
            setShowAlert(false);
            return undefined;
        }

        // Readiness only matters when pricing comes from categories.
        if (isCarPricingMode) {
            setShowAlert(false);
            return undefined;
        }

        let cancelled = false;

        (async () => {
            try {
                const { data } = await apiClient.get("/api/car-category-v2/pricing-readiness-alert");
                if (cancelled) return;

                setShowAlert(Boolean(data?.data?.showPricingAlert));
            } catch (_error) {
                if (!cancelled) {
                    setShowAlert(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isCarPricingMode, myCompanyData, refreshKey, state?.isAuthenticated]);

    const handleCloseAlert = () => {
        setShowAlert(false);
    };

    const handleNavigateToCategories = () => {
        history.push("/cars?tab=categories");
    };

    if (isCarPricingMode || !showAlert) {
        return null;
    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: "#ff9800",
                    colorInfoBg: "#ff9800",
                    colorText: "#000000",
                    colorIcon: "#000000",
                },
            }}>
            <Alert
                className={BANNER_ALERT_COMPACT_CLASS}
                message={
                    <BannerAlertMessage
                        actionLabel={t("categoryPricingAlert.button", "Go to Categories")}
                        onAction={handleNavigateToCategories}>
                        {t("categoryPricingAlert.message", "Some of your car categories are missing pricing details.")}{" "}
                        <span className="font-semibold">
                            {t("categoryPricingAlert.action", "Please complete their pricing")}
                        </span>{" "}
                        {t("categoryPricingAlert.continuation", "so customers can book these vehicles.")}
                    </BannerAlertMessage>
                }
                banner
                type="info"
                closable
                afterClose={handleCloseAlert}
                showIcon={false}
            />
        </ConfigProvider>
    );
};

CategoryPricingReadinessAlert.propTypes = {
    refreshKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
};

export default CategoryPricingReadinessAlert;
